import React, { useState } from 'react';
import { Globe, Check, ChevronDown, Loader2 } from 'lucide-react';
import { useLanguage, Language, languageNames } from '../contexts/LanguageContext';

// Language flag emojis for visual appeal
const languageFlags: Record<string, string> = {
    en: '🇺🇸', es: '🇪🇸', fr: '🇫🇷', de: '🇩🇪', ru: '🇷🇺',
    zh: '🇨🇳', hi: '🇮🇳', ar: '🇸🇦', pt: '🇧🇷', ja: '🇯🇵',
    ko: '🇰🇷', it: '🇮🇹', nl: '🇳🇱', tr: '🇹🇷', pl: '🇵🇱',
    vi: '🇻🇳', th: '🇹🇭', uk: '🇺🇦', sv: '🇸🇪', cs: '🇨🇿',
};

interface LanguageSelectorProps {
    showLabel?: boolean;
    variant?: 'dropdown' | 'select' | 'compact' | 'minimal';
    className?: string;
}

const LanguageSelector: React.FC<LanguageSelectorProps> = ({
    showLabel = false,
    variant = 'dropdown',
    className = '',
}) => {
    const { language, setLanguage, availableLanguages, isTranslating } = useLanguage();
    const [isOpen, setIsOpen] = useState(false);

    if (variant === 'select') {
        return (
            <div className={className}>
                {showLabel && (
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Language
                    </label>
                )}
                <div className="relative">
                    <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <select
                        value={language}
                        onChange={(e) => setLanguage(e.target.value as Language, { fromUser: true })}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none cursor-pointer"
                    >
                        {availableLanguages.map((lang) => (
                            <option key={lang} value={lang}>
                                {languageFlags[lang] || ''} {languageNames[lang]}
                            </option>
                        ))}
                    </select>
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none flex items-center gap-1">
                        {isTranslating && <Loader2 className="w-3 h-3 text-blue-500 animate-spin" />}
                        <ChevronDown className="w-4 h-4 text-gray-400" />
                    </div>
                </div>
            </div>
        );
    }

    if (variant === 'minimal') {
        return (
            <div className={`relative ${className}`}>
                <button
                    onClick={() => setIsOpen(!isOpen)}
                    className="flex items-center gap-1.5 px-2 py-1.5 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    aria-label="Change language"
                >
                    <span className="text-base">{languageFlags[language] || '🌐'}</span>
                    <span className="uppercase text-xs font-bold tracking-wide">{language}</span>
                    {isTranslating && <Loader2 className="w-3 h-3 text-blue-500 animate-spin" />}
                </button>

                {isOpen && (
                    <>
                        <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
                        <div className="absolute right-0 mt-1 w-56 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl z-50 overflow-hidden">
                            <div className="py-1.5 max-h-80 overflow-y-auto">
                                {availableLanguages.map((lang) => (
                                    <button
                                        key={lang}
                                        onClick={() => { setLanguage(lang, { fromUser: true }); setIsOpen(false); }}
                                        className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2.5 transition-colors ${
                                            language === lang
                                                ? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white font-medium'
                                                : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                                        }`}
                                    >
                                        <span className="text-base w-6 text-center">{languageFlags[lang] || '🌐'}</span>
                                        <span className="flex-1">{languageNames[lang]}</span>
                                        {language === lang && <Check className="w-4 h-4 text-blue-600" />}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </>
                )}
            </div>
        );
    }

    if (variant === 'compact') {
        return (
            <div className={`relative ${className}`}>
                <button
                    onClick={() => setIsOpen(!isOpen)}
                    className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    aria-label="Change language"
                >
                    <Globe className="w-4 h-4" />
                    <span className="font-medium">{languageNames[language]}</span>
                    {isTranslating && <Loader2 className="w-3 h-3 text-blue-500 animate-spin" />}
                    <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                </button>

                {isOpen && (
                    <>
                        <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
                        <div className="absolute right-0 mt-1 w-64 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl z-50 overflow-hidden">
                            <div className="px-3 py-2 bg-gray-50 dark:bg-gray-900 border-b border-gray-100 dark:border-gray-700">
                                <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Select Language</p>
                            </div>
                            <div className="py-1 max-h-72 overflow-y-auto">
                                {availableLanguages.map((lang) => (
                                    <button
                                        key={lang}
                                        onClick={() => { setLanguage(lang, { fromUser: true }); setIsOpen(false); }}
                                        className={`w-full text-left px-3 py-2.5 text-sm flex items-center gap-3 transition-colors ${
                                            language === lang
                                                ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 font-medium'
                                                : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                                        }`}
                                    >
                                        <span className="text-lg w-7 text-center">{languageFlags[lang] || '🌐'}</span>
                                        <span className="flex-1">{languageNames[lang]}</span>
                                        {language === lang && <Check className="w-4 h-4 text-blue-600" />}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </>
                )}
            </div>
        );
    }

    // Default: dropdown variant - grid of buttons
    return (
        <div className={className}>
            {showLabel && (
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Language {isTranslating && <Loader2 className="inline w-3 h-3 text-blue-500 animate-spin ml-1" />}
                </label>
            )}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
                {availableLanguages.map((lang) => (
                    <button
                        key={lang}
                        onClick={() => setLanguage(lang, { fromUser: true })}
                        className={`px-3 py-2 text-sm rounded-lg transition-all flex items-center gap-2 ${
                            language === lang
                                ? 'bg-gray-900 text-white shadow-md'
                                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                        }`}
                    >
                        <span className="text-base">{languageFlags[lang] || '🌐'}</span>
                        <span className="truncate">{languageNames[lang]}</span>
                    </button>
                ))}
            </div>
        </div>
    );
};

export default LanguageSelector;
